// M11, H6-01, D-230 : unités entières de demi-journée, calendrier explicitement configuré.
// Aucune règle d'acquisition officielle ni aucun calendrier national n'est prérempli.
import { z } from 'zod';
import { schemaJourCivil } from './proprete';

const jourConge = schemaJourCivil.refine((jour) => {
  const date = new Date(`${jour}T00:00:00Z`);
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === jour;
}, 'Date civile impossible');

export const TYPES_CONGE = ['annuel', 'maladie', 'maternite', 'exceptionnel'] as const;
export const schemaTypeConge = z.enum(TYPES_CONGE);
export const schemaRegleConge = z
  .strictObject({
    libelle: z.string().trim().min(3).max(160),
    type: schemaTypeConge,
    annee: z.number().int().min(2000).max(2200),
    // ISO : lundi 1 à dimanche 7. La liste doit être saisie par l'administration.
    jours_decomptes: z
      .array(z.number().int().min(1).max(7))
      .min(1)
      .max(7)
      .refine((jours) => new Set(jours).size === jours.length, 'Jour répété'),
    jours_exclus: z
      .array(jourConge)
      .max(366)
      .refine((jours) => new Set(jours).size === jours.length, 'Date répétée'),
    solde_requis: z.boolean(),
    reference_administrative: z.string().trim().min(3).max(250),
  })
  .superRefine((regle, contexte) => {
    if (regle.jours_exclus.some((jour) => !jour.startsWith(`${regle.annee}-`))) {
      contexte.addIssue({
        code: 'custom',
        path: ['jours_exclus'],
        message: 'Date hors année de la règle',
      });
    }
  });

export const schemaPeriodeConge = z
  .strictObject({
    debut: jourConge,
    debut_demi_journee: z.enum(['matin', 'apres_midi']),
    fin: jourConge,
    fin_demi_journee: z.enum(['matin', 'apres_midi']),
  })
  .superRefine((periode, contexte) => {
    if (
      periode.fin < periode.debut ||
      (periode.fin === periode.debut &&
        periode.debut_demi_journee === 'apres_midi' &&
        periode.fin_demi_journee === 'matin')
    ) {
      contexte.addIssue({ code: 'custom', path: ['fin'], message: 'La fin précède le début' });
    }
    if (periode.debut.slice(0, 4) !== periode.fin.slice(0, 4)) {
      contexte.addIssue({
        code: 'custom',
        path: ['fin'],
        message: 'Créer une demande distincte pour chaque année',
      });
    }
  });

export const schemaDemandeConge = z.strictObject({
  agent_id: z.string().uuid(),
  regle_id: z.string().uuid(),
  periode: schemaPeriodeConge,
  motif: z.string().trim().max(2000).optional(),
});
export const schemaDecisionConge = z
  .strictObject({
    decision: z.enum(['valider', 'refuser']),
    motif: z.string().trim().max(1000).optional(),
  })
  .refine((d) => d.decision !== 'refuser' || (d.motif?.length ?? 0) >= 3, {
    path: ['motif'],
    message: 'Un refus doit être motivé',
  });
export const schemaMouvementSoldeConge = z.strictObject({
  agent_id: z.string().uuid(),
  type: schemaTypeConge,
  annee: z.number().int().min(2000).max(2200),
  demi_journees: z
    .number()
    .int()
    .min(-732)
    .max(732)
    .refine((n) => n !== 0, 'Mouvement nul'),
  motif: z.string().trim().min(3).max(1000),
  cle_idempotence: z.string().uuid(),
});
export const schemaFiltreSoldeConge = z.strictObject({
  type: schemaTypeConge,
  annee: z.coerce.number().int().min(2000).max(2200),
  avant: z.string().uuid().optional(),
});
export type RegleConge = z.infer<typeof schemaRegleConge>;
export type PeriodeConge = z.infer<typeof schemaPeriodeConge>;
